import {
    useQuery,
    useQueryClient,
    useMutation,
    UseMutationResult,
} from '@tanstack/react-query';

import {
    createWidget,
    moveWidgets,
    PayloadWidgetT,
} from 'lib/layout/provider/backend';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { ARGUMENT_CONTAINER } from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/Argument/hooks/constants';
import { RQAPICallWithData } from 'store/APIClient';
import { LayoutT } from 'business/definitions';

type UseLayoutT = {
    widgets: WidgetT[];
    setWidgets: (arg1: WidgetT[]) => void;
    moveWidgetsMutation: UseMutationResult<WidgetT[], unknown, void, unknown>;
    addWidgetMutation: UseMutationResult<
        WidgetT,
        unknown,
        PayloadWidgetT,
        unknown
    >;
    loading: boolean;
    error: boolean;
};

export const useArgumentLayout = (layoutId: number | null): UseLayoutT => {
    const queryClient = useQueryClient();

    const {
        data: widgetsData = [],
        isInitialLoading: isWidgetsLoading,
        isError: isWidgetsError,
    } = useQuery({
        queryKey: ['widgets', layoutId],
        queryFn: async () => {
            if (layoutId === null) return [];
            const response = await RQAPICallWithData<LayoutT>({
                endpoint: `explore/layouts/argument/${layoutId}`,
                method: 'GET',
            });
            return response.content.data.widgets;
        },
        enabled: layoutId !== null,
    });
    const setWidgets = (widgets: WidgetT[]) =>
        queryClient.setQueryData(['widgets', layoutId], widgets);

    const moveWidgetsMutation = useMutation({
        mutationFn: () => {
            if (layoutId === null) {
                return Promise.reject(new Error('layoutId cannot be null'));
            }
            return moveWidgets(
                ARGUMENT_CONTAINER,
                layoutId,
                widgetsData.map((w) => ({ key: w.key, position: w.position })),
            );
        },
    });

    const addWidgetMutation = useMutation({
        mutationFn: (payload: PayloadWidgetT) => {
            if (layoutId === null) {
                return Promise.reject(new Error('layoutId cannot be null'));
            }
            return createWidget(ARGUMENT_CONTAINER, layoutId, payload);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['widgets', layoutId] }),
    });

    return {
        widgets: widgetsData,
        setWidgets,
        moveWidgetsMutation,
        addWidgetMutation,
        loading: isWidgetsLoading,
        error: isWidgetsError,
    };
};
